import { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { api, publicApi } from '@/lib/api';
import { useAuth } from '@/lib/auth-context';
import { CHARACTER } from '@/constants/testIds';
import { ArrowLeft, Bookmark, BookmarkCheck, Share2, Check, Eye, EyeOff, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const ICON_PLACEHOLDER = '/images/icon-placeholder.png';

export default function CharacterDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [character, setCharacter] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [bookmarked, setBookmarked] = useState(false);
  const [flashStatus, setFlashStatus] = useState(null);

useEffect(() => {
  let cancelled = false;
  const maxRetries = 4;
  const retryDelay = 2000;

  const fetchCharacter = (attempt) => {
    if (cancelled) return;
    setLoading(true);
      publicApi.get(`/characters/${id}`)
        .then(({ data }) => {
        if (cancelled) return;
        setCharacter(data);
        setLoadError(null);
        setLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        const status = err?.response?.status;
        if (status === 404) {
          setLoadError('not_found');
          setLoading(false);
          return;
        }
        if (status === 503 && attempt < maxRetries) {
          setTimeout(() => fetchCharacter(attempt + 1), retryDelay);
          return;
        }
        setLoadError('timeout');
        setLoading(false);
      });
  };

  setCharacter(null);
  setLoadError(null);
  fetchCharacter(0);

  return () => { cancelled = true; };
}, [id]);
  
  useEffect(() => {
    if (!user) return;
    api.get('/bookmarks').then(({ data }) => {
      setBookmarked(!!data.items.find((c) => c.id === id));
    });
    api.get('/flashcards').then(({ data }) => {
      const f = data.items.find((x) => x.character_id === id);
      setFlashStatus(f?.status || null);
    });
  }, [user, id]);

  const handleBookmark = async () => {
    if (!user) { navigate('/login'); return; }
    try {
      if (bookmarked) {
        await api.delete(`/bookmarks/${id}`);
        setBookmarked(false);
        toast.success('Removed from bookmarks');
      } else {
        await api.post(`/bookmarks/${id}`);
        setBookmarked(true);
        toast.success('Bookmarked');
      }
    } catch {
      toast.error('Action failed');
    }
  };

  const handleShare = async () => {
    const url = window.location.href;
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Link copied to clipboard');
    } catch {
      toast.error('Could not copy link');
    }
  };

  const handleFlashcardStatus = async (status) => {
    if (!user) { navigate('/login'); return; }
    try {
      await api.post(`/flashcards/${id}`, { status });
      setFlashStatus(status);
      const labels = { known: 'Marked as Known', want_to_know: 'Added to Want to Know', ignore: 'Ignored' };
      toast.success(labels[status]);
    } catch {
      toast.error('Could not update');
    }
  };

if (loading) {
  return (
    <div className="flex flex-col items-center justify-center gap-3 py-32">
      <Loader2 className="w-6 h-6 text-slate-400 animate-spin" />
      <p className="text-sm text-slate-500">Loading character...</p>
    </div>
  );
}
if (loadError === 'not_found') {
  return (
    <div className="container-page py-24 text-center">
    <p className="font-serif-display text-3xl text-slate-700 mb-2">Person not found</p>
      <Link to="/" className="text-[#1E3A8A] hover:underline text-sm">← Back to directory</Link>
    </div>
    );
}
if (loadError === 'timeout' || !character) {
  return (
    <div className="container-page py-24 text-center">
    <p className="font-serif-display text-3xl text-slate-700 mb-2">Taking longer than expected</p>
      <p className="text-sm text-slate-500 mb-4">The server may still be waking up. Please try again in a moment.</p>
      <button onClick={() => window.location.reload()} className="text-[#1E3A8A] hover:underline text-sm mr-4">Retry</button>
      <Link to="/" className="text-[#1E3A8A] hover:underline text-sm">← Back to directory</Link>
    </div>
    );
}

  const isMajor = character.importance === 'Major';
  const isOT = character.appearances?.[0]?.testament === 'Old';

  const statusBtn = (key, label, Icon, color) => (
    <button
      onClick={() => handleFlashcardStatus(key)}
      className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-md border text-sm font-medium transition-all ${
        flashStatus === key
          ? `${color} text-white border-transparent`
          : 'bg-white text-slate-700 border-slate-200 hover:border-slate-400'
      }`}
    >
      <Icon className="w-4 h-4" /> {label}
    </button>
  );

  return (
    <div className="container-page py-10 lg:py-14">
      <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-[#1E3A8A] mb-8 transition-colors">
        <ArrowLeft className="w-4 h-4" /> Back to directory
      </Link>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14">
        {/* Icon slot */}
        <div className="lg:col-span-4">
          <div
            data-testid={CHARACTER.iconSlot}
            className="aspect-square rounded-2xl bg-gradient-to-br from-[#CCFBF1] via-[#DBEAFE] to-[#F8FAFC] border border-slate-200 p-6 flex items-center justify-center relative overflow-hidden"
          >
            <img
              src={character.image_url || ICON_PLACEHOLDER}
              alt={character.name}
              className="w-full h-full object-contain"
            />
            {!character.image_url && (
              <span className="absolute bottom-3 right-3 text-[10px] text-slate-500 uppercase tracking-widest bg-white/80 backdrop-blur px-2 py-0.5 rounded">
                placeholder icon
              </span>
            )}
          </div>

          <div className="mt-6 space-y-2">
            <button
              data-testid={CHARACTER.bookmarkButton}
              onClick={handleBookmark}
              className={`w-full flex items-center justify-center gap-2 px-4 py-3 rounded-md border text-sm font-medium transition-all ${
                bookmarked
                  ? 'bg-[#1E3A8A] text-white border-[#1E3A8A]'
                  : 'bg-white text-slate-700 border-slate-200 hover:border-[#1E3A8A]'
              }`}
            >
              {bookmarked ? <BookmarkCheck className="w-4 h-4" /> : <Bookmark className="w-4 h-4" />}
              {bookmarked ? 'Bookmarked' : 'Bookmark'}
            </button>
            <button
              data-testid={CHARACTER.shareButton}
              onClick={handleShare}
              className="w-full btn-secondary text-sm py-3"
            >
              <Share2 className="w-4 h-4" /> Copy share link
            </button>
          </div>
        </div>

        {/* Main details */}
        <div className="lg:col-span-8">
          <div className="flex flex-wrap items-center gap-2 mb-4">
            <span
              data-testid={CHARACTER.importanceTag}
              className={`tag-base ${isMajor ? 'bg-[#DBEAFE] text-[#1E3A8A]' : 'bg-slate-100 text-slate-600'}`}
            >
              {character.importance} figure
            </span>
            <span
              data-testid={CHARACTER.testamentTag}
              className={`tag-base ${isOT ? 'bg-amber-50 text-amber-700' : 'bg-[#CCFBF1] text-[#0F766E]'}`}
            >
              {isOT ? 'Old Testament' : 'New Testament'}
            </span>
            <span className="tag-base bg-slate-50 text-slate-700">{character.appearances?.[0]?.book}</span>
          </div>

          <h1
            data-testid={CHARACTER.detailName}
            className="font-serif-display text-5xl sm:text-6xl tracking-tight text-slate-900 mb-3"
          >
            {character.name}
          </h1>

          <div
            data-testid={CHARACTER.detailScripture}
            className="font-mono text-sm text-[#0F766E] mb-8"
          >
            {character.appearances?.[0]?.scripture_reference}
          </div>

          <div className="prose prose-slate max-w-none">
            <p
              data-testid={CHARACTER.detailBody}
              className="text-lg leading-relaxed text-slate-700"
            >
              {character.appearances?.[0]?.details}
            </p>
          </div>
          
          {/* Flashcard status */}
          <div className="mt-10 pt-8 border-t border-slate-100">
            <h3 className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-500 mb-4">
              Study Status
            </h3>
            <div className="flex flex-col sm:flex-row gap-2">
              {statusBtn('known', 'Known', Check, 'bg-emerald-600')}
              {statusBtn('want_to_know', 'Want to Know', Eye, 'bg-amber-500')}
              {statusBtn('ignore', 'Ignore', EyeOff, 'bg-rose-500')}
            </div>
            {!user && (
              <p className="text-xs text-slate-500 mt-3">
                <Link to="/login" className="text-[#1E3A8A] hover:underline">Sign in</Link> to track your study progress.
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
